/* iter-53o — Extracted from PublishToBookstoreDialog.jsx to keep the parent
 * dialog under 400 LOC. This component owns all "publish form" state (genre,
 * price, royalty, language, back-copy, sample, cover-tier, custom-cover
 * brief/refs, override URL) plus the two handlers that operate on that state
 * (`onPayCustomCover` and `onPublish`). The parent only supplies the
 * dropdown catalogs and calls back via `onPublished` so it can refresh the
 * editing-status.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BookOpen, CheckCircle2, CreditCard, Image as ImageIcon, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { editingApi } from '@/lib/api';

export default function PublishBookForm({
  project, coverTiers, genres, languages, customOrder, onPublished,
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [genreSlug, setGenreSlug] = useState(genres[0]?.slug || '');
  const [priceUsd, setPriceUsd] = useState('14.99');
  const [backCopy, setBackCopy] = useState('');
  const [sampleText, setSampleText] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [originalLanguage, setOriginalLanguage] = useState('en');
  const [royaltyPct, setRoyaltyPct] = useState(85);
  const [coverTierId, setCoverTierId] = useState('basic');
  const [coverBrief, setCoverBrief] = useState('');
  const [coverRefs, setCoverRefs] = useState('');

  const selectedTier = useMemo(
    () => coverTiers.find(t => t.id === coverTierId) || null,
    [coverTiers, coverTierId],
  );
  const customPaid = !!(customOrder && customOrder.payment_status === 'paid');

  const onPayCustomCover = async () => {
    if (!project) return;
    if (!coverBrief.trim()) {
      toast.error(t('publish.coverBriefRequired'));
      return;
    }
    setBusy(true);
    try {
      const refs = coverRefs.split(/[\s,]+/).map(s => s.trim()).filter(Boolean).slice(0, 8);
      const r = await editingApi.coverCustomCheckout(project.id, {
        origin_url: window.location.origin,
        cover_brief: coverBrief,
        cover_reference_urls: refs,
      });
      window.location.href = r.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('publish.coverPaymentFailed'));
    } finally { setBusy(false); }
  };

  const onPublish = async () => {
    if (!genreSlug) { toast.error(t('publish.pickGenre')); return; }
    const cents = Math.round(parseFloat(priceUsd || '0') * 100);
    if (!cents || cents < 0) { toast.error(t('publish.setValidPrice')); return; }
    if (coverTierId === 'custom' && !customPaid) {
      toast.error(t('publish.payCustomCoverFirst'));
      return;
    }
    setBusy(true);
    try {
      const refs = coverRefs.split(/[\s,]+/).map(s => s.trim()).filter(Boolean).slice(0, 8);
      const r = await editingApi.publish(project.id, {
        price_cents: cents,
        genre_slug: genreSlug,
        back_copy: backCopy,
        sample_text: sampleText,
        cover_front_url: coverUrl,
        original_language: originalLanguage,
        royalty_pct: parseInt(royaltyPct, 10) || 85,
        cover_art_tier_id: coverTierId,
        cover_brief: coverBrief,
        cover_reference_urls: refs,
      });
      toast.success(t('publish.nowListed', { title: r.book.title }));
      await onPublished?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.detail || t('publish.publishFailed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-md border border-emerald-300 bg-emerald-50/40 p-3">
      <div className="text-[12px] font-semibold text-emerald-900 flex items-center gap-1.5">
        <CheckCircle2 className="h-4 w-4" /> {t('publish.editingComplete')}
      </div>

      {/* Basic publishing details */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">{t('publish.genre')}</Label>
          <select className="h-9 w-full rounded-sm border border-border bg-background px-2 text-[12.5px]"
                  value={genreSlug} onChange={e => setGenreSlug(e.target.value)}
                  data-testid="publish-genre-select">
            <option value="">{t('publish.selectEllipsis')}</option>
            {genres.map(g => <option key={g.id || g.slug} value={g.slug}>{g.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">{t('publish.retailPrice')}</Label>
          <Input type="number" step="0.01" min="0"
                 data-testid="publish-price-input"
                 value={priceUsd} onChange={e => setPriceUsd(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">{t('publish.originalLanguage')}</Label>
          <select className="h-9 w-full rounded-sm border border-border bg-background px-2 text-[12.5px]"
                  data-testid="publish-language-select"
                  value={originalLanguage} onChange={e => setOriginalLanguage(e.target.value)}>
            {(languages.length ? languages : [{ code: 'en', name: 'English' }]).map(l => (
              <option key={l.code} value={l.code}>{l.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[11px]">{t('publish.authorRoyalty')}</Label>
          <Input type="number" min="0" max="100"
                 data-testid="publish-royalty-input"
                 value={royaltyPct} onChange={e => setRoyaltyPct(e.target.value)} />
        </div>
      </div>

      <div>
        <Label className="text-[11px]">{t('publish.backCoverCopy')}</Label>
        <Textarea rows={2} value={backCopy} onChange={e => setBackCopy(e.target.value)}
                  data-testid="publish-back-copy" />
      </div>
      <div>
        <Label className="text-[11px]">{t('publish.sampleText')}</Label>
        <Textarea rows={3} value={sampleText} onChange={e => setSampleText(e.target.value)}
                  data-testid="publish-sample-text" />
      </div>

      {/* Cover Art tier picker */}
      <div className="rounded-md border border-border bg-card p-2.5">
        <div className="flex items-center gap-1.5 text-[11.5px] font-semibold">
          <ImageIcon className="h-3.5 w-3.5" /> {t('publish.coverArt')}
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          {t('publish.coverArtHint')}
        </p>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
          {coverTiers.map(t2 => {
            const active = coverTierId === t2.id;
            return (
              <button key={t2.id} type="button"
                      data-testid={`publish-cover-tier-${t2.id}`}
                      onClick={() => setCoverTierId(t2.id)}
                      className={`text-left rounded-sm border p-2 transition ${active ? 'border-primary bg-primary/10' : 'border-border bg-background hover:border-primary/50'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold">{t2.name}</span>
                  <span className="font-mono-stat text-[11px]">{t2.price_usd > 0 ? `$${t2.price_usd.toFixed(2)}` : t('publish.free')}</span>
                </div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">{t2.desc}</div>
              </button>
            );
          })}
        </div>

        {selectedTier?.id !== 'basic' && (
          <div className="mt-2 space-y-1.5">
            <div>
              <Label className="text-[11px]">{t('publish.coverBrief')}</Label>
              <Textarea rows={2} value={coverBrief} onChange={e => setCoverBrief(e.target.value)}
                        data-testid="publish-cover-brief"
                        placeholder={t('publish.coverBriefPlaceholder')} />
            </div>
            <div>
              <Label className="text-[11px]">{t('publish.referenceImageUrls')}</Label>
              <Textarea rows={2} value={coverRefs} onChange={e => setCoverRefs(e.target.value)}
                        data-testid="publish-cover-refs"
                        placeholder="https://… https://…" />
            </div>
          </div>
        )}

        {selectedTier?.id === 'custom' && !customPaid && (
          <div className="mt-2 space-y-1.5 rounded-sm border border-amber-300 bg-amber-50 p-2">
            <div className="text-[11.5px] text-amber-900 flex items-center gap-1.5">
              <Lock className="h-3.5 w-3.5" /> {t('publish.customCoverRequiresPayment', { price: selectedTier.price_usd.toFixed(2) })}
            </div>
            <Button size="sm" className="gap-1.5 w-full"
                    data-testid="publish-cover-pay-custom-btn"
                    disabled={busy} onClick={onPayCustomCover}>
              <CreditCard className="h-3.5 w-3.5" /> {t('publish.payWithStripe', { price: selectedTier.price_usd.toFixed(2) })}
            </Button>
          </div>
        )}
        {selectedTier?.id === 'custom' && customPaid && (
          <div className="mt-2 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900 flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t('publish.customCoverPaid')}
          </div>
        )}
      </div>

      <div>
        <Label className="text-[11px]">{t('publish.coverImageUrl')}</Label>
        <Input type="url" placeholder="https://…"
               data-testid="publish-cover-url"
               value={coverUrl} onChange={e => setCoverUrl(e.target.value)} />
      </div>
      <Button onClick={onPublish} disabled={busy} className="w-full gap-1.5"
              data-testid="editing-publish-btn">
        <BookOpen className="h-3.5 w-3.5" /> {t('publish.publishToBookstore')}
      </Button>
    </div>
  );
}
